import { cva, type VariantProps } from "class-variance-authority";
import type { HTMLAttributes } from "react";
import { forwardRef } from "react";
import { cn } from "@/shared/utils/cn";

export const alertVariants = cva("relative w-full rounded-lg border p-4 text-sm", {
  variants: {
    variant: {
      default: "border-border bg-background text-foreground",
      info: "border-alert-blue/30 bg-alert-blue/10 text-clm-primary-blue",
      // Text/background pairs use the ux-design-guidelines surface tints so
      // alert body text meets WCAG AA (T086 audit: the previous /10 tints
      // measured 3.29:1-4.39:1; these pairs measure 4.79:1-5.91:1).
      success: "border-alert-green/30 bg-alert-green-lightest text-alert-green",
      warning: "border-alert-yellow/30 bg-alert-yellow-lightest text-alert-yellow",
      destructive: "border-alert-red/30 bg-alert-red-lightest text-alert-red",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export interface AlertProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof alertVariants> {}

export const Alert = forwardRef<HTMLDivElement, AlertProps>(function Alert(
  { className, variant, role = "alert", ...props },
  ref,
) {
  // role defaults to "alert" (assertive live region); callers may override
  // (e.g. role="note" for visual-only callouts, role="status" for polite
  // success confirmation) when another region owns the announcement.
  return (
    <div ref={ref} role={role} className={cn(alertVariants({ variant, className }))} {...props} />
  );
});

export type AlertTitleProps = HTMLAttributes<HTMLHeadingElement> & {
  /**
   * Heading level for the title. Defaults to h5 (existing usage); set the
   * level that fits the surrounding document outline (issue #81).
   */
  as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
};

/** Title styling, exported for the rare non-heading title (shell notice host). */
export const alertTitleClassName = "mb-1 font-semibold leading-none tracking-tight";

export const AlertTitle = forwardRef<HTMLHeadingElement, AlertTitleProps>(function AlertTitle(
  { className, as: Heading = "h5", ...props },
  ref,
) {
  return <Heading ref={ref} className={cn(alertTitleClassName, className)} {...props} />;
});

export const AlertDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(function AlertDescription({ className, ...props }, ref) {
  return <div ref={ref} className={cn("text-sm [&_p]:leading-relaxed", className)} {...props} />;
});
